import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { nonNullable } from '@rma/generic/rxjs/operators/non-nullable';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { catchError, filter, map, Observable, of, take, tap } from 'rxjs';
import { ContextSelectorService } from './context-selector.service';
import { CurrentUserApiService } from './current-user-api.service';
import { AvailableProfile, AvailableProfiles, AvailableProfilesRequest } from './domain/available-profiles.model';
import { CurrentUser } from './domain/current-user.models';
import { CurrentUserContext } from './domain/profile-context.models';
import { InitialContextService } from './initial-context.service';

@Injectable({
  providedIn: 'root',
})
export class CurrentUserService {
  private readonly apiService = inject(CurrentUserApiService);
  private readonly initialContextService = inject(InitialContextService);
  private readonly contextSelector = inject(ContextSelectorService);

  // `undefined` is the not-yet-loaded sentinel: it is filtered out of the observable shims so they keep the
  // ReplaySubject's "no emission until the first load" behaviour. `null` is the loaded-but-unauthenticated state.
  private readonly userState = signal<CurrentUser | null | undefined>(undefined);

  public readonly currentUser = computed(() => this.userState() ?? null);
  public readonly currentUserContext = computed(() => this.mapUserToContext(this.currentUser()));
  public readonly currentUserDefaultProfile = computed(() => this.mapUserToDefaultProfile(this.currentUser()));
  public readonly isAuthenticated = computed(() => !!this.currentUser());

  // The other signal selectors collapse the not-yet-loaded sentinel into `null`/`false`, so they can't distinguish
  // "still loading" from "logged out". Gate on `isLoaded()` before trusting them for first-paint auth decisions.
  public readonly isLoaded = computed(() => this.userState() !== undefined);

  /**
   * @deprecated Use the {@link currentUser} signal instead. Note the semantic difference: this stream stays silent
   * until the first load completes, whereas the signal reports `null` (not-yet-loaded collapsed to logged-out) eagerly.
   */
  public readonly currentUser$ = toObservable(this.userState).pipe(filter((user): user is CurrentUser | null => user !== undefined));
  /**
   * @deprecated Use the {@link currentUserContext} signal instead. Like {@link currentUser$}, this stream stays silent
   * until the first load, while the signal reports `null` eagerly.
   */
  public readonly currentUserContext$ = this.currentUser$.pipe(map((user) => this.mapUserToContext(user)));
  /**
   * @deprecated Use the {@link currentUserDefaultProfile} signal instead. The signal is NOT a drop-in replacement: it
   * returns `null` when there is no default profile, whereas this stream filters those out via `nonNullable()`. Add a
   * null check when migrating.
   */
  public readonly currentUserDefaultProfile$ = this.currentUser$.pipe(
    map((user) => this.mapUserToDefaultProfile(user)),
    nonNullable(),
  );
  /**
   * @deprecated Use the {@link isAuthenticated} signal instead. Note the semantic difference: this stream stays silent
   * until the first load completes, whereas the signal reports `false` eagerly (not-yet-loaded collapsed to logged-out).
   */
  public readonly isAuthenticated$ = this.currentUser$.pipe(map((x) => !!x));

  public getAvailableProfiles(type: 'Agent' | 'Agency', request: AvailableProfilesRequest = { take: 10 }): Observable<AvailableProfiles> {
    return type === 'Agent' ? this.apiService.getAvailableAgentProfiles(request) : this.apiService.getAvailableAgencyProfiles(request);
  }

  public reloadUser(): Observable<CurrentUser | null> {
    const reloadedUser$ = this.apiService.getCurrentUser().pipe(
      catchError((e) => {
        console.warn('Auth check failed', e);
        return of(null);
      }),
      tap((x) => this.userState.set(x)),
      rmaShareReplay(),
    );
    reloadedUser$.pipe(take(1)).subscribe(); // ensure it happens
    return reloadedUser$;
  }

  public onLogout() {
    this.userState.set(null);
    this.contextSelector.setContext(undefined);
  }

  private mapUserToContext(user: CurrentUser | null): CurrentUserContext | null {
    if (!user) {
      return null;
    }

    return {
      alias: user.alias,
      agents: user.agents,
      email: user.email,
      firstname: user.firstname,
      hasAssistantAccess: user.hasAssistantAccess,
      hasMultipleProfiles: user.hasMultipleProfiles,
      hasSeenWelcomeModal: user.hasSeenWelcomeModal,
      isImpersonating: user.isImpersonating,
      lastLoggedIn: user.lastLoggedIn,
      lastname: user.lastname,
      name: user.name ?? user.alias,
      timeZoneId: user.timeZoneId,
      userId: user.userId,
      userType: user.userType,
    };
  }

  private mapUserToDefaultProfile(user: CurrentUser | null): AvailableProfile | null {
    if (!user) {
      return null;
    }

    const defaultContext = this.initialContextService.getDefaultContext(user);

    if (!defaultContext) {
      return null;
    }

    return {
      profileType: defaultContext.profileType,
      profileCode: defaultContext.profileCode,
      profileImage: user.profileImage,
      name: user.name ?? user.alias,
    };
  }
}
